import { useState } from 'react';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { EditAliasesDialog } from './edit-aliases-dialog';
import { Permission } from '@/../../backend/src/auth/permissions/permission';
import { PersonType, normalisePersonReferences } from '@/../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useDeletePerson } from '@/api/mutations/useDeletePerson';
import { useUpdatePerson } from '@/api/mutations/useUpdatePerson';
import { Person } from '@/api/types/backend-dto-types';
import { capitalizeFirstLetter } from '@/helpers/filterNames';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Chip } from '@/new/components/chip';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { EditFieldDialog } from '@/new/components/edit-field-dialog';
import { Icon } from '@/new/components/icon';
import { Badge } from '@/new/components/shadcn/badge';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { TextWithReferences } from '@/new/components/text-references';
import { cn } from '@/new/lib/utils';

const getPersonTypeBadgeProps = (type: PersonType) => {
  switch (type) {
    case PersonType.Person:
      return {
        className: 'text-xs bg-blue-100 text-blue-800 border-blue-200',
        iconClass: 'lucide--user-round',
      };
    case PersonType.Organisation:
      return {
        className: 'text-xs bg-purple-100 text-purple-800 border-purple-200',
        iconClass: 'lucide--building',
      };
    case PersonType.Place:
      return {
        className: 'text-xs bg-teal-100 text-teal-700 border-teal-200',
        iconClass: 'lucide--map-pin',
      };
  }
};

export const EntityRow = ({ person }: { person: Person }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { matterId, subsetId } = useParams();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [aliasesDialogOpen, setAliasesDialogOpen] = useState(false);

  const { mutateAsync: updatePerson } = useUpdatePerson({
    matterId: matterId!,
    subsetId: subsetId!,
  });

  const { mutateAsync: deletePerson, isLoading: isDeleting } = useDeletePerson({
    matterId: matterId!,
    subsetId: subsetId!,
  });

  const handleExploreFacts = (personId: string) => {
    trackCustomEvent(TrackingEventsV2.FactFilterByPerson);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('personId', personId);
    navigate(`/matters/${matterId}/subsets/${subsetId}/facts?${newSearchParams.toString()}`);
  };

  const handleDelete = async () => {
    await deletePerson({ personId: person.id });
    setDeleteDialogOpen(false);
  };

  return (
    <div className="relative rounded-md border border-gray-200">
      {/* Header Section */}
      <div className="flex items-center justify-between rounded-t-md border-b bg-gray-50 py-3 pr-3 pl-4.5">
        <h3 className="flex items-center gap-3 text-base font-medium text-gray-700">
          <Icon className={cn(getPersonTypeBadgeProps(person.type).iconClass, 'text-sm text-gray-500')} />
          <span>{person.name}</span>
          <PermissionGate permission={Permission.PersonUpdate} uiMode="disabled">
            <EditFieldDialog
              title="Edit Entity Name"
              description="Update the name for this entity."
              fieldLabel="Name"
              currentValue={person.name}
              placeholder="Entity name"
              onSave={(value) => updatePerson({ personId: person.id, name: value.trim() })}
            />
          </PermissionGate>
        </h3>

        <div className="flex items-center gap-2">
          <Chip className={cn(getPersonTypeBadgeProps(person.type).className, 'border')}>
            {capitalizeFirstLetter(person.type)}
          </Chip>

          <PermissionGate permission={Permission.PersonDelete} uiMode="disabled">
            <button
              className="rounded-md p-1.5 text-gray-400 hover:text-red-600"
              onClick={() => setDeleteDialogOpen(true)}
            >
              <Icon className="lucide--trash-2 text-sm" />
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col gap-2 p-4">
        {/* Aliases Section */}
        <div className="flex items-center gap-1 text-sm">
          <span className="font-semibold text-gray-600">Aliases:</span>
          <div className="flex min-w-0 items-center gap-1">
            {person.aliases && person.aliases.length > 0 ? (
              <>
                <span className="truncate text-gray-700">{person.aliases.slice(0, 4).join(', ')}</span>
                {person.aliases.length > 4 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-700 hover:underline">
                        +{person.aliases.length - 4} more
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto max-w-lg p-3">
                      <div className="text-sm text-gray-700">
                        <div className="mb-1 font-medium">All aliases:</div>
                        <div className="text-xs">{person.aliases.slice(4).join(', ')}</div>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
              </>
            ) : (
              <span className="text-gray-400 italic">None</span>
            )}
          </div>
          <PermissionGate permission={Permission.PersonUpdate} uiMode="disabled">
            <button
              className="ml-1 rounded-md p-1 text-gray-400 hover:text-gray-800"
              onClick={() => setAliasesDialogOpen(true)}
              aria-label="Edit aliases"
            >
              <Icon className="lucide--pencil text-xs" />
            </button>
          </PermissionGate>
        </div>

        {/* Description Section */}
        {person.description && (
          <div className="text-sm text-gray-600">
            <TextWithReferences
              content={person.description}
              referencesDict={normalisePersonReferences(person.references) ?? undefined}
            />
          </div>
        )}

        {/* Footer Section */}
        <div className="mt-1 flex items-center justify-between">
          <Button onClick={() => handleExploreFacts(person.id)} className="text-xs">
            View Related Facts
            <Icon className="iconify lucide--arrow-right" />
          </Button>
        </div>
      </div>

      {/* Edit Aliases Dialog */}
      <EditAliasesDialog
        open={aliasesDialogOpen}
        onOpenChange={setAliasesDialogOpen}
        entityName={person.name}
        currentAliases={person.aliases ?? []}
        onSave={(aliases) => updatePerson({ personId: person.id, aliases })}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationModal
        isOpen={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Entity"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to delete <strong>{person.name}</strong>? This action cannot be undone.
        </p>
      </ConfirmationModal>
    </div>
  );
};
